import { Box, BoxProps, Image } from '@chakra-ui/react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image';
import * as React from 'react';

const DIRECTUS_ORIGIN = 'https://acm.directus.cobtech.de';
const DIRECTUS_ASSET_ID_PATTERN =
  /(?:^|\/assets\/)([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})(?:[/?#]|$)/i;

type DirectusFile = {
  id: string;
  imageFile?: {
    childImageSharp?: {
      gatsbyImageData: IGatsbyImageData;
    } | null;
  } | null;
};

type DirectusImagesQuery = {
  directus_system: {
    files: DirectusFile[];
  };
};

export type DirectusImageProps = Omit<BoxProps, 'children'> & {
  assetId?: string | null;
  src?: string | null;
  alt: string;
  loading?: 'eager' | 'lazy';
  objectFit?: React.CSSProperties['objectFit'];
  objectPosition?: React.CSSProperties['objectPosition'];
};

const extractDirectusAssetId = (src?: string | null) => src?.match(DIRECTUS_ASSET_ID_PATTERN)?.[1];

const directusAssetUrl = (assetId: string) => `${DIRECTUS_ORIGIN}/assets/${assetId}`;

/**
 * Serves Directus images through Gatsby's build-time image pipeline.
 *
 * A single static query creates responsive JPEG/WebP variants up to 1920px for
 * every image in the Directus file library. This also lets Markdown images,
 * whose asset IDs only exist inside a text field, resolve to optimized files.
 */
export const DirectusImage: React.FC<DirectusImageProps> = ({
  assetId,
  src,
  alt,
  loading = 'lazy',
  objectFit = 'cover',
  objectPosition = 'center center',
  ...boxProps
}) => {
  const data = useStaticQuery<DirectusImagesQuery>(graphql`
    query OptimizedDirectusImages {
      directus_system {
        files(filter: { _and: [{ type: { _starts_with: "image/" } }, { type: { _neq: "image/svg+xml" } }] }) {
          id
          imageFile {
            childImageSharp {
              gatsbyImageData(
                layout: FULL_WIDTH
                breakpoints: [320, 480, 768, 1024, 1440, 1920]
                quality: 78
                formats: [AUTO, WEBP]
                placeholder: BLURRED
              )
            }
          }
        }
      }
    }
  `);

  const resolvedAssetId = assetId ?? extractDirectusAssetId(src);
  const file = resolvedAssetId ? data.directus_system.files.find(item => item.id === resolvedAssetId) : undefined;
  const imageData = file?.imageFile?.childImageSharp?.gatsbyImageData;
  const preserveIntrinsicRatio = objectFit === 'contain';

  if (imageData) {
    return (
      <Box
        w="full"
        h={preserveIntrinsicRatio ? 'auto' : 'full'}
        overflow={preserveIntrinsicRatio ? 'visible' : 'hidden'}
        {...boxProps}
      >
        <GatsbyImage
          image={imageData}
          alt={alt}
          loading={loading}
          objectFit={objectFit}
          objectPosition={objectPosition}
          style={{ width: '100%', height: preserveIntrinsicRatio ? 'auto' : '100%' }}
        />
      </Box>
    );
  }

  const fallbackSrc = src ?? (resolvedAssetId ? directusAssetUrl(resolvedAssetId) : undefined);

  if (!fallbackSrc) {
    return null;
  }

  return (
    <Image
      src={fallbackSrc}
      alt={alt}
      loading={loading}
      objectFit={objectFit}
      objectPosition={objectPosition}
      w="full"
      h={preserveIntrinsicRatio ? 'auto' : 'full'}
      {...boxProps}
    />
  );
};
